/**
 * @documental
 * @export Badge
 * @import
 * import { Badge } from "capitalrx-components-2.0/lib/general";
 */
import { FC, ReactNode } from "react";

import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import classNames from "classnames";

import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Button, ButtonClickEventHandler } from "../Button";
import { Tag, TagProps } from "../Tag";
import { getBadgeCloseButtonAppearance } from "./BadgeCloseButton";

import tokenStyles from "./styles.module.css";

/**
 * The `Badge` component properties.
 * A badge always renders the light neutral tag style, so it never sets a tag type.
 */
export interface BadgeProps extends Omit<TagProps, "type" | "inverted"> {
  /**
   * Called when the close button is clicked.
   */
  readonly onClick?: ButtonClickEventHandler;

  /**
   * Indicates whether the badge is disabled.
   */
  readonly disabled?: boolean;

  /**
   * The badge tooltip.
   */
  readonly tooltip?: ReactNode;

  /**
   * The close button tooltip.
   */
  readonly buttonTooltip?: ReactNode;
}

const useResolvedBadgeStyles = createComponentStyleHook<BadgeProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "badge",
});

/**
 * A badge.
 */
export const Badge: FC<BadgeProps> = (props) => {
  const { children, tooltip, buttonTooltip, onClick, disabled = false } = props;
  const { mode, isLegacy, styles, StyleModeBoundary } = useResolvedBadgeStyles(props);
  const basePropsForHtml = isLegacy ? props : { ...props, className: undefined, style: undefined };

  const baseHtmlProps = getHtmlPropsFromBaseProps(basePropsForHtml, {
    className: [classNames(styles.Badge, "badge", { [styles.BadgeDisabled]: disabled })],
    testIdPrefix: "badge",
  });

  const { className: internalClassName, style: internalStyle, ...tagBaseProps } = getBasePropsFromHtmlProps(baseHtmlProps);
  const closeButtonClassName = isLegacy ? classNames(styles.CloseButton, "info") : undefined;
  const closeButtonAppearance = isLegacy ? "text" : getBadgeCloseButtonAppearance();

  return (
    <StyleModeBoundary mode={mode}>
      <Tag
        {...props}
        {...tagBaseProps}
        className={internalClassName}
        style={internalStyle}
        tooltip={tooltip}
        aria-live={disabled ? undefined : "polite"}
        aria-disabled={disabled}
      >
        <Shelf spacing="mini">
          <span className={classNames(styles.Content)}>{children}</span>
          {!disabled && (
            // TODO: add key handlers to buttons
            <Button
              className={closeButtonClassName}
              appearance={closeButtonAppearance}
              icon="x"
              size="small"
              tooltip={buttonTooltip}
              onClick={onClick}
              aria-label="Close Badge"
            />
          )}
        </Shelf>
      </Tag>
    </StyleModeBoundary>
  );
};
